import React from "react";
import { useTranslation } from "react-i18next";
import { useRestaurant } from "./RestaurantContext";

// Keys for src/locales/{en,fr}.json under "help": each renders as
// `${key}Q` (question) + `${key}A` (answer) in an expandable <details>.
const COMMON_FAQS = ["reply", "filterReviews", "twofa", "password", "language", "stats"];
const ADMIN_FAQS = ["invite", "managerPermissions", "reviewLink", "qr", "landing", "sponsor", "analytics", "cardOrder", "google", "resetManagerPw", "delete"];
// The topics a manager can now be delegated (Fred, 2026-09-15; cardOrder
// added 2026-09-17). Shown to a manager who currently holds ANY delegated
// permission at any location - not split further per-permission, since
// showing all of them once any one is granted is a reasonable
// simplification (worst case a manager reads about a feature they don't
// have yet, which isn't a security concern, just a help page).
const DELEGABLE_FAQS = ["qr", "landing", "sponsor", "analytics", "cardOrder"];

/**
 * In-app FAQ / getting-started help: a real page in the shared layout
 * (like every other nav item), not a popup, so it behaves consistently with
 * the rest of the nav. Admins and managers only; App.tsx keeps superadmin off
 * the /help route and out of the nav, since superadmin is an internal platform
 * operator, not a customer needing product help.
 */
const Help: React.FC = () => {
  const { t } = useTranslation();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user.role === "admin";
  const { canManageQr, canEditLandingPage, canViewAnalytics, canOrderCards } = useRestaurant();
  const hasDelegatedAccess = canManageQr || canEditLandingPage || canViewAnalytics || canOrderCards;

  // Most answers are a single string. A few (ones that walk through several
  // distinct sub-features, like Campaign Analytics's sections) are instead
  // { intro, items } so they render as an intro line plus a bullet list -
  // much easier to scan than one long run-on sentence.
  const renderFaq = (key: string) => {
    const answer = t(`help.${key}A`, { returnObjects: true }) as string | { intro: string; items: string[] };
    return (
      <details key={key} className="border rounded-lg p-3 bg-white group">
        <summary className="font-medium cursor-pointer text-gray-800 marker:text-gray-400">
          {t(`help.${key}Q`)}
        </summary>
        {typeof answer === "string" ? (
          <p className="text-sm text-gray-600 mt-2">{answer}</p>
        ) : (
          <div className="text-sm text-gray-600 mt-2">
            <p>{answer.intro}</p>
            <ul className="list-disc pl-5 mt-2 space-y-1.5">
              {answer.items.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
        )}
      </details>
    );
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">{t("help.title")}</h1>

      <div className="space-y-2">{COMMON_FAQS.map(renderFaq)}</div>

      {isAdmin && (
        <>
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-6 pb-2">
            {t("help.adminSectionTitle")}
          </h2>
          <div className="space-y-2">{ADMIN_FAQS.map(renderFaq)}</div>
        </>
      )}

      {!isAdmin && hasDelegatedAccess && (
        <>
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-6 pb-2">
            {t("help.delegatedSectionTitle")}
          </h2>
          <div className="space-y-2">{DELEGABLE_FAQS.map(renderFaq)}</div>
        </>
      )}
    </div>
  );
};

export default Help;
